import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { stepChanged } from "@/redux/slices/dreamUniversitySlice";
import { ArrowLeft, ArrowRight, Home } from "lucide-react";
import { useTranslation } from "react-i18next";
import { requiresGenderSelection } from "../data/universityRegistry";

/** Shared back-navigation row for wizard steps: "back to previous" plus,
 *  on steps deeper than the second, "back to first step". Going back never
 *  clears selections — a full reset only happens when the student picks a
 *  different university on the first step. */
export default function BackControls({ onBack }: { onBack: () => void }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        locale,
        dreamUniversity: { universityId, activeStepId },
    } = useAppSelector((state) => state);

    const secondStepId = !universityId
        ? null
        : requiresGenderSelection(universityId)
            ? "gender"
            : universityId === "foreign_missions"
                ? "fm_country"
                : universityId === "internal_missions"
                    ? "im_institution"
                    : "college";

    const showBackToFirst =
        activeStepId !== "university" && activeStepId !== secondStepId;

    const goToFirstStep = () => {
        dispatch(stepChanged("university"));
        setTimeout(() => {
            document
                .getElementById("dream-university-wizard")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    return (
        <div className="flex flex-col items-start gap-2">
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-sm font-medium text-theme-700 hover:text-theme-800 transition-colors"
            >
                {locale.isRTL ? (
                    <ArrowRight className="w-4 h-4" />
                ) : (
                    <ArrowLeft className="w-4 h-4" />
                )}
                {t("dream_university_back")}
            </button>
            {showBackToFirst && (
                <button
                    onClick={goToFirstStep}
                    className="flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 hover:bg-emerald-100 hover:text-emerald-800 rounded-full px-3.5 py-1.5 transition-colors"
                >
                    <Home className="w-4 h-4" />
                    {t("dream_university_back_to_first")}
                </button>
            )}
        </div>
    );
}
